'use client'

// How he sees you, as a thing that happened on a day.
//
// This used to be a card at the top of the Mentions tab, which is the
// wrong room: mentions are the things he said, and a ladder explaining
// what "a familiar face" means is not one of them. Sam, 25 Sep: "the
// description of the users standing (a familiar face for example) could
// go under the you tab, and could even be another popup that explains it
// as a timeline."
//
// So it is a second drawer over the first, opened from the portrait on
// the You tab, and it is dated. The rungs are counts (3 kept, 20 kept,
// 60 kept) and /api/profile now ships when he kept each of yours, so the
// day you crossed a rung is a real date rather than a tick in a list.
// That is the whole difference: a badge tells you what you are, a
// timeline tells you what happened and when, and one of those is worth
// opening twice.
//
// Undated rungs are said as undated. An account restored from before
// judged_at existed has kept offerings with no time on them, and putting
// a plausible date on a rung would be inventing a memory.

import { Drawer, Card, Pill } from './Drawer'
import { kawaii } from '../../lib/ui/theme'
import { BIRD_NAME } from '../../lib/world/identity'
import {
  TIERS, resolveTier, nextTier, submitsPerDay, postersAllowed,
  CALENDAR_POWERS, influenceMultiple,
} from '../../lib/tiers'

// One line per rung, composed from the real numbers rather than written
// out again: the thresholds are env-tunable and a second copy of
// "20 means posters" would be wrong the first time somebody moved one.
// Anything behind a flow that is not built yet is marked, not hidden.
function powersFor(tier) {
  const mult = influenceMultiple(tier.name)
  const out = [
    // First, because it is the only one that is not an allowance: the
    // others give you more of him, this one puts you inside his head.
    mult > 1
      ? `${mult === 1.5 ? 'half again as much' : `${mult}x the`} pull on what he thinks about`
      : 'your gifts start pulling on what he thinks about',
    `${submitsPerDay(tier.name)} ${submitsPerDay(tier.name) === 1 ? 'offering' : 'offerings'} a day`,
    `${tier.actionsPerDay} things a day at places around town`,
  ]
  const posters = postersAllowed(tier.name)
  if (posters) out.push(`make and keep ${posters} posters`)
  for (const p of CALENDAR_POWERS) {
    if (p.min > 0 && p.min === tier.min) out.push(p.built ? p.label : `${p.label} (coming)`)
  }
  return out
}

// The year only when it is not this one. Almost every climb happens
// inside a few weeks, and "4 September 2026" down a column of them is
// the same four characters repeated for no reader.
function day(ts, now = Date.now()) {
  const d = new Date(ts)
  const sameYear = d.getFullYear() === new Date(now).getFullYear()
  return d.toLocaleDateString(undefined, sameYear
    ? { day: 'numeric', month: 'long' }
    : { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * The day you crossed a rung: the day he kept your Nth thing, where N is
 * the rung's threshold. Credits (see standing_credits) count towards the
 * total without being offerings, so they shift which kept offering was
 * the one that did it.
 */
export function crossedAt(min, kept = [], credited = 0) {
  if (min <= 0) return null
  const i = min - credited - 1
  if (i < 0) return null
  return kept?.[i] ?? null
}

/** The dot in the gutter. Filled when you have been there. */
function Node({ on, here, color }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: here ? 18 : 14,
        height: here ? 18 : 14,
        marginTop: 3,
        borderRadius: 999,
        flex: 'none',
        background: on ? color : kawaii.cream,
        border: kawaii.outlineThin,
        boxShadow: here ? kawaii.dropSm : 'none',
      }}
    />
  )
}

function Rung({ tier, kept, credited, keptCount, here, last }) {
  const reached = keptCount >= tier.min
  const at = reached ? crossedAt(tier.min, kept, credited) : null
  const toGo = Math.max(0, tier.min - keptCount)

  return (
    <div style={{ display: 'flex', gap: 12 }}>
      {/* The gutter: the node, and the line down to the next one. Not a
          border on the row, which would be an accent rail wearing a
          different hat. */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none', width: 18 }}>
        <Node on={reached} here={here} color={tier.color} />
        {!last && (
          <span
            aria-hidden="true"
            style={{ flex: 1, width: 0, minHeight: 16, borderLeft: reached ? kawaii.outlineThin : kawaii.stitch, marginTop: 4 }}
          />
        )}
      </div>

      <div className="min-w-0" style={{ paddingBottom: last ? 0 : 20 }}>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <span style={{ color: reached ? tier.color : kawaii.inkSoft, fontSize: 15.5, fontWeight: 800, lineHeight: 1.3 }}>
            {tier.name}
          </span>
          <span style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 700 }}>
            {tier.min === 0
              ? 'the day you arrived'
              : at
                ? day(at)
                : reached
                  ? `${tier.min} kept`
                  : `${toGo} more to go`}
          </span>
        </div>
        <p style={{ color: kawaii.ink, fontSize: 13, fontWeight: 600, lineHeight: 1.5, marginTop: 2 }}>
          {tier.subtitle}.
        </p>
        <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600, lineHeight: 1.55, marginTop: 4, opacity: reached ? 1 : 0.8 }}>
          {powersFor(tier).join(' · ')}
        </p>
        {here && (
          <p style={{ color: kawaii.ink, fontSize: 12.5, fontWeight: 800, marginTop: 5 }}>
            you are here.
          </p>
        )}
      </div>
    </div>
  )
}

export function StandingDrawer({ data, onClose }) {
  const standing = data?.standing || { accepted: 0 }
  const keptCount = standing.accepted || 0
  const credited = standing.credited || 0
  const kept = data?.kept || []
  const here = data?.tier?.name
    ? TIERS.find((t) => t.name === data.tier.name) || resolveTier(keptCount)
    : resolveTier(keptCount)
  const up = nextTier(here.name)
  const toGo = up ? Math.max(0, up.min - keptCount) : 0
  // Oldest rung first: a climb reads upwards, and a timeline that starts
  // at the end is a list of results, not a story.
  const climb = [...TIERS].reverse()

  return (
    <Drawer
      level={1}
      title="How he sees you"
      right={<Pill bg={here.color}>{here.name}</Pill>}
      subtitle="what he has kept of yours, and when"
      onClose={onClose}
      height="min(74dvh, 780px)"
    >
      <div className="mx-auto w-full max-w-2xl space-y-5 pt-1">
        <Card>
          <p style={{ color: kawaii.ink, fontSize: 15, fontWeight: 700, lineHeight: 1.55 }}>
            {keptCount === 0
              ? `${BIRD_NAME} has not kept anything of yours yet. Leave him something at the shrine and he will decide for himself.`
              : up
                ? <>He has kept <strong>{keptCount}</strong> {keptCount === 1 ? 'thing' : 'things'} of yours. {toGo} more and he would call you <strong style={{ color: up.color }}>{up.name.toLowerCase()}</strong>.</>
                : <>He has kept <strong>{keptCount}</strong> things of yours. There is no rung above this one.</>}
          </p>
          <p style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 600, lineHeight: 1.55, marginTop: 8 }}>
            Anything he keeps goes into what he is interested in, and the better
            he knows you, the harder it pulls. That is not a badge: it steers what
            he reads about online, which films he picks, what he takes up, even
            what his drawings come out looking like.
          </p>
          <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600, lineHeight: 1.55, marginTop: 8 }}>
            He only counts what he decided to keep. A no costs you nothing, it
            just does not count, and he always says what did not land. Nothing
            here can be bought.
          </p>
        </Card>

        <div>
          <p style={{ color: kawaii.inkSoft, fontSize: 11.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 12 }}>
            how you got here
          </p>
          {climb.map((t, i) => (
            <Rung
              key={t.name}
              tier={t}
              kept={kept}
              credited={credited}
              keptCount={keptCount}
              here={t.name === here.name}
              last={i === climb.length - 1}
            />
          ))}
        </div>

        <p style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 600, lineHeight: 1.55, paddingBottom: 4 }}>
          The other ladder, how well you read {BIRD_NAME}, is counted separately
          and never buys his affection.
        </p>
      </div>
    </Drawer>
  )
}

export default StandingDrawer
